/**
 * useResetQueryCacheOnUserChange — wipes the React Query cache when the
 * authenticated user identity changes, to prevent a cross-user data leak.
 *
 * Why this exists
 * ---------------
 * Each app owns ONE module-level QueryClient (apps/*\/src/app/router.tsx). On an
 * SPA sign-out → sign-in IN THE SAME TAB there is no full page reload, so that
 * client — and every cached, user-scoped entry in it (notifications, bookings,
 * profile, …) — survives. Without an explicit reset, User B who signs in right
 * after User A signed out would momentarily read A's warm cache (A's unread
 * badge, A's inbox rows = names/booking PII) before B's own refetch lands.
 * Server-side scoping is correct; the client cache is the hole. AuthProvider
 * lives ABOVE the QueryClientProvider, so it cannot clear the client directly —
 * this hook is mounted INSIDE the provider where both `useQueryClient()` and
 * `useAuth()` are in scope.
 *
 * Behaviour
 * ---------
 * It gates descendants until the first settled identity has synchronized its
 * payment owner. On every later identity transition (including A→B, A→null,
 * and null→B), descendants unmount first; a layout effect then advances payment
 * ownership, cancels Feed reconciliation/pins, clears the query cache, and
 * releases the new identity's subtree.
 * No previous observer can retain data after `QueryClient.clear()`, and no new
 * user's request can capture the previous payment generation.
 *
 * The same settled boundary owns session-persisted payment-method detach and
 * uncertain-outcome fences. They survive a reload for one user but are cleared
 * before another identity can inherit Stripe object identifiers.
 */
import { useLayoutEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { resetFeedCacheForIdentityChange } from "./feedCache";
import { synchronizePaymentMethodDetachBarrierOwner } from "./payment/paymentMethodDetachBarrier";
import { useAuth } from "./useAuth";
import { synchronizeCompanyMethodIntentActor } from "../utils/companyMethodIntent";
import { synchronizeCompanyPurchaseIntentActor } from "../utils/companyPurchaseIntent";

export function useResetQueryCacheOnUserChange(): boolean {
  const queryClient = useQueryClient();
  const { user, loading } = useAuth();
  const currentUserId = loading ? undefined : (user?.uid ?? null);
  // `undefined` = not yet observed; `null` = observed signed-out; string = uid.
  const previousUserId = useRef<string | null | undefined>(undefined);
  const [isolatedUserId, setIsolatedUserId] = useState<
    string | null | undefined
  >(undefined);

  useLayoutEffect(() => {
    if (currentUserId === undefined) return;
    synchronizeCompanyMethodIntentActor(currentUserId);
    synchronizeCompanyPurchaseIntentActor(currentUserId);
    synchronizePaymentMethodDetachBarrierOwner(currentUserId);
    const prev = previousUserId.current;

    if (prev !== undefined && prev !== currentUserId) {
      resetFeedCacheForIdentityChange(queryClient);
      queryClient.clear();
    }
    previousUserId.current = currentUserId;
    setIsolatedUserId(currentUserId);
  }, [currentUserId, queryClient]);

  return currentUserId !== undefined && isolatedUserId === currentUserId;
}
